import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Input,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
  Spinner,
  Box,
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaCheck } from 'react-icons/fa';
import { User } from '@supabase/supabase-js';
import { UserType } from '../../utils/userUtils';
import { deleteUserAccount } from '../../lib/api/userApi';
import { useAuth } from '../../lib/auth/AuthProvider';
import { trackEvent } from '../../lib/analytics';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  userType: UserType;
  isLocal: boolean;
  linkedProviders: string[];
}

type DeleteStep = 1 | 2 | 3;

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  user,
  userType,
  isLocal,
  linkedProviders,
}) => {
  const { t } = useTranslation(['v3/account', 'v3/common']);
  const toast = useToast();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  // 状态管理
  const [currentStep, setCurrentStep] = useState<DeleteStep>(1);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 重置状态当弹窗关闭时
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setConfirmText('');
      setPassword('');
      setIsDeleting(false);
      setErrors({});
    }
  }, [isOpen]);

  // 检查是否需要密码验证
  const needsPasswordVerification = !isLocal && user?.email && linkedProviders.includes('email');

  // 验证步骤1的确认文本
  const validateStep1 = (): boolean => {
    const expectedText = t('v3/account:deleteConfirmation.step1.placeholder');
    if (confirmText.trim() !== expectedText) {
      setErrors({ confirmText: t('v3/account:deleteConfirmation.errors.confirmTextMismatch') });
      return false;
    }
    setErrors({});
    return true;
  };

  // 验证步骤2的密码
  const validateStep2 = (): boolean => {
    if (!password.trim()) {
      setErrors({ password: t('v3/account:deleteConfirmation.errors.passwordRequired') });
      return false;
    }
    setErrors({});
    return true;
  };

  // 处理下一步
  const handleNext = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        // 如果不需要密码验证，直接跳到步骤3
        setCurrentStep(needsPasswordVerification ? 2 : 3);
      }
    } else if (currentStep === 2) {
      if (validateStep2()) {
        setCurrentStep(3);
      }
    }
  };

  // 处理返回
  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 3) {
      setCurrentStep(needsPasswordVerification ? 2 : 1);
    }
    setErrors({});
  };

  // 执行删除
  const handleDelete = async () => {
    setIsDeleting(true);
    setErrors({});

    try {
      // 记录删除尝试事件
      await trackEvent('account deletion attempt', {
        user_type: userType,
        is_local: isLocal,
        linked_providers: linkedProviders,
      });

      // 调用删除 API
      await deleteUserAccount();

      // 记录删除成功事件
      await trackEvent('account deletion success', {
        user_type: userType,
        is_local: isLocal,
      });

      // 显示成功消息
      toast({
        title: t('v3/account:deleteConfirmation.success.title'),
        description: t('v3/account:deleteConfirmation.success.message'),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // 关闭弹窗
      onClose();

      // 登出并跳转到登录页
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('删除账号失败:', error);

      // 记录删除失败事件
      await trackEvent('account deletion failed', {
        user_type: userType,
        is_local: isLocal,
        error: error instanceof Error ? error.message : String(error),
      });

      setErrors({
        general: error instanceof Error ? error.message : t('v3/account:deleteConfirmation.errors.deleteFailed'),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // 渲染步骤1：警告确认
  const renderStep1 = () => (
    <VStack spacing={4} align="stretch">
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text>{t('v3/account:deleteConfirmation.step1.irreversibleWarning')}</Text>
      </Alert>

      <Text>{t('v3/account:deleteConfirmation.step1.warning')}</Text>

      <List spacing={2}>
        {t('v3/account:deleteConfirmation.step1.consequences', { returnObjects: true }).map(
          (consequence: string, index: number) => (
            <ListItem key={index}>
              <ListIcon as={FaCheck} color="red.500" />
              {consequence}
            </ListItem>
          )
        )}
      </List>

      <Box>
        <Text mb={2}>{t('v3/account:deleteConfirmation.step1.confirmText')}</Text>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={t('v3/account:deleteConfirmation.step1.placeholder')}
          isInvalid={!!errors.confirmText}
        />
        {errors.confirmText && (
          <Text color="red.500" fontSize="sm" mt={1}>
            {errors.confirmText}
          </Text>
        )}
      </Box>
    </VStack>
  );

  // 渲染步骤2：密码验证
  const renderStep2 = () => (
    <VStack spacing={4} align="stretch">
      <Text>{t('v3/account:deleteConfirmation.step2.description')}</Text>

      <Input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t('v3/account:deleteConfirmation.step2.passwordPlaceholder')}
        isInvalid={!!errors.password}
      />
      {errors.password && (
        <Text color="red.500" fontSize="sm">
          {errors.password}
        </Text>
      )}

      {!needsPasswordVerification && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text>{t('v3/account:deleteConfirmation.step2.hint')}</Text>
        </Alert>
      )}
    </VStack>
  );

  // 渲染步骤3：最终确认
  const renderStep3 = () => (
    <VStack spacing={4} align="stretch">
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Text>{t('v3/account:deleteConfirmation.step3.finalWarning')}</Text>
      </Alert>

      <Text fontWeight="bold">{t('v3/account:deleteConfirmation.step3.question')}</Text>

      <Box>
        <Text mb={2}>{t('v3/account:deleteConfirmation.step3.dataLoss')}</Text>
        <List spacing={1} fontSize="sm" color="gray.600">
          <ListItem>• 项目和任务数据</ListItem>
          <ListItem>• 聊天记录</ListItem>
          <ListItem>• 个人设置</ListItem>
          <ListItem>• 第三方账号绑定</ListItem>
        </List>
      </Box>

      {errors.general && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Text>{errors.general}</Text>
        </Alert>
      )}
    </VStack>
  );

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return t('v3/account:deleteConfirmation.step1.title');
      case 2:
        return t('v3/account:deleteConfirmation.step2.title');
      case 3:
        return t('v3/account:deleteConfirmation.step3.title');
      default:
        return '';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={!isDeleting}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <FaExclamationTriangle color="red" />
            <Text>{getStepTitle()}</Text>
          </HStack>
        </ModalHeader>

        <ModalBody>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            {currentStep > 1 && (
              <Button variant="ghost" onClick={handleBack} isDisabled={isDeleting}>
                {t('v3/account:deleteConfirmation.actions.back')}
              </Button>
            )}

            <Button variant="ghost" onClick={onClose} isDisabled={isDeleting}>
              {currentStep === 3
                ? t('v3/account:deleteConfirmation.actions.reconsider')
                : t('v3/account:deleteConfirmation.actions.cancel')}
            </Button>

            {currentStep < 3 ? (
              <Button colorScheme="red" onClick={handleNext}>
                {currentStep === 2
                  ? t('v3/account:deleteConfirmation.actions.verify')
                  : t('v3/account:deleteConfirmation.actions.next')}
              </Button>
            ) : (
              <Button
                colorScheme="red"
                onClick={handleDelete}
                isLoading={isDeleting}
                loadingText={t('v3/common:loading')}
                leftIcon={isDeleting ? <Spinner size="sm" /> : undefined}
              >
                {t('v3/account:deleteConfirmation.actions.confirmDelete')}
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
